import { z } from "zod";

/**
 * The browser's sign-in configuration, served by the public `GET /api/auth/config` (Session 9,
 * contract §2). One web image serves either identity provider: the server reads it from the
 * `AUTH_CLIENT` environment variable at runtime, and the client fetches it before MSAL starts.
 *
 * `AUTH_CLIENT` names the ONE issuer the browser signs in with. `IDP_ISSUERS` separately lists
 * every issuer the API trusts; `issuerName` must name one of those entries by its `name`.
 *
 * The values are public by nature (an MSAL client id, an authority URL, scopes): nothing here is
 * a secret, which is why the route needs no authentication.
 */

/** The identity-provider families. Also the claim profiles an `IDP_ISSUERS` entry may name. */
export const AUTH_IDPS = ["entra-external", "entra-workforce", "custom"] as const;

export const authClientConfigSchema = z.object({
  idp: z.enum(AUTH_IDPS),
  /** The `name` of an `IDP_ISSUERS` entry. */
  issuerName: z.string().min(1),
  authority: z.string().url(),
  /** The SPA app's id (its API is named in `scopes`). */
  clientId: z.string().min(1),
  /** Filters the MSAL account cache to this directory. External ID: the tenant GUID. */
  tenantId: z.string().min(1).optional(),
  scopes: z.array(z.string().min(1)).min(1),
  /**
   * Host names, never URLs. Lower-cased on parse, so the browser gets exactly the value the
   * server's start-up checks compared (they compare hosts without case).
   */
  knownAuthorities: z
    .array(z.string().min(1))
    .min(1)
    .transform((hosts) => hosts.map((host) => host.trim().toLowerCase())),
  /** Absent means `${origin}/callback`. */
  redirectUri: z.string().url().optional(),
  postLogoutRedirectUri: z.string().url().optional(),
});
export type AuthClientConfig = z.infer<typeof authClientConfigSchema>;

/**
 * One way to sign in, in the providers form of `AUTH_CLIENT` (workforce SSO, 2026-09-28): the
 * single object above plus a stable key, the button text, an optional `domain_hint` and a
 * default flag.
 *
 * Every provider is its own MSAL client in the browser, because the authority differs (External
 * ID's `ciamlogin.com` host, Microsoft's `login.microsoftonline.com/organizations`). A provider
 * that differs only by `domainHint` (Google federated inside External ID) repeats the External
 * ID provider's authority and client id and adds `domain_hint` to the request.
 */
export const authProviderSchema = authClientConfigSchema.extend({
  /** Stable key: persisted in the browser and used in test ids. Lower case, digits, hyphens. */
  id: z.string().regex(/^[a-z0-9][a-z0-9-]{0,31}$/, "id must be 1-32 characters of [a-z0-9-]"),
  /** The button text on the sign-in page. */
  label: z.string().trim().min(1).max(64),
  /** Sent as `domain_hint`, e.g. `google.com` for a Google IdP federated inside External ID. */
  domainHint: z.string().trim().min(1).max(253).optional(),
  /** The provider the email form starts, and the one every new grant is pinned to. */
  default: z.boolean().optional(),
  /**
   * Round 9: a new grant to an address in one of these domains pins to THIS provider instead of
   * the default (dev: the e2e accounts' `skypointaidev.onmicrosoft.com` stay on External ID).
   * Lower-case domains, no `@`. Since #1639 C0 it also defaults the grant dialog's "Sign in with"
   * (`defaultSignInMethod`), which the admin may change.
   */
  pinEmailDomains: z
    .array(
      z
        .string()
        .trim()
        .toLowerCase()
        .regex(/^[a-z0-9-]+(\.[a-z0-9-]+)+$/, "a domain, without @"),
    )
    .min(1)
    .optional(),
});
export type AuthProvider = z.infer<typeof authProviderSchema>;

/**
 * Where "Contact Support" points: a `mailto:` or an `https:` URL and nothing else, because the
 * sign-in page puts it in an `href`.
 */
export const supportUrlSchema = z
  .string()
  .trim()
  .max(512)
  .refine((value) => /^(mailto:[^\s@]+@[^\s@]+|https:\/\/\S+)$/i.test(value), {
    message: "supportUrl must be a mailto: or https: URL",
  });

/** What "Contact Support" points at when the deployment names nothing. */
export const DEFAULT_SUPPORT_URL = "https://skypoint.ai/customer-support/";

/** The providers form of `AUTH_CLIENT`: `{"providers":[...],"supportUrl"?:...}`. */
export const authClientProvidersSchema = z
  .object({
    providers: z.array(authProviderSchema).min(1),
    supportUrl: supportUrlSchema.optional(),
  })
  .superRefine((value, ctx) => {
    const ids = value.providers.map((provider) => provider.id);
    const duplicate = ids.find((id, index) => ids.indexOf(id) !== index);
    if (duplicate) {
      ctx.addIssue({ code: "custom", path: ["providers"], message: `id "${duplicate}" repeats` });
    }
    const domains = value.providers.flatMap((provider) => provider.pinEmailDomains ?? []);
    const repeated = domains.find((domain, index) => domains.indexOf(domain) !== index);
    if (repeated) {
      ctx.addIssue({
        code: "custom",
        path: ["providers"],
        message: `pinEmailDomains "${repeated}" is on two providers`,
      });
    }
    if (value.providers.filter((provider) => provider.default).length > 1) {
      ctx.addIssue({
        code: "custom",
        path: ["providers"],
        message: "at most one provider may be the default",
      });
    }
  });

/** The default provider: the one marked `default`, else the first. */
export function defaultAuthProvider<T extends Pick<AuthProvider, "default">>(
  providers: readonly T[],
): T {
  const found = providers.find((provider) => provider.default) ?? providers[0];
  if (!found) throw new Error("an auth client lists no providers");
  return found;
}

/**
 * `AUTH_CLIENT` as the server holds it and as `GET /api/auth/config` serves it.
 *
 * The top level is always the single-object shape: in the single form it is the whole value,
 * unchanged; in the providers form it is the DEFAULT provider's fields. So the grant pin
 * (`authClientIssuer`) keeps reading `issuerName`, and a browser still running a bundle from
 * before the providers form parses the answer as it always did and signs in with the default.
 * `providers` and `supportUrl` are present only in the providers form.
 */
export const authClientSettingSchema = authClientConfigSchema.extend({
  providers: z.array(authProviderSchema).min(1).optional(),
  supportUrl: supportUrlSchema.optional(),
});
export type AuthClientSetting = z.infer<typeof authClientSettingSchema>;

export type AuthClientSettingParse =
  { success: true; data: AuthClientSetting } | { success: false; error: z.ZodError };

/** Only the single-object fields of a provider. */
function singleFields(provider: AuthProvider): AuthClientConfig {
  const { id: _id, label: _label, domainHint: _hint, default: _default, ...rest } = provider;
  return rest;
}

/**
 * Parse either form of `AUTH_CLIENT` into the setting shape. The providers form is recognised by
 * its `providers` key; anything else is read as the single object, exactly as before.
 */
export function parseAuthClientSetting(json: unknown): AuthClientSettingParse {
  if (json !== null && typeof json === "object" && "providers" in json) {
    const parsed = authClientProvidersSchema.safeParse(json);
    if (!parsed.success) return { success: false, error: parsed.error };
    const fallback = defaultAuthProvider(parsed.data.providers);
    return {
      success: true,
      data: {
        ...singleFields(fallback),
        providers: parsed.data.providers,
        ...(parsed.data.supportUrl ? { supportUrl: parsed.data.supportUrl } : {}),
      },
    };
  }
  const single = authClientConfigSchema.safeParse(json);
  return single.success
    ? { success: true, data: single.data }
    : { success: false, error: single.error };
}

/** Every provider of a setting. The single form is one provider: id `default`, label "Sign in". */
export function authProvidersOf(setting: AuthClientSetting): AuthProvider[] {
  if (setting.providers) return setting.providers;
  const { providers: _providers, supportUrl: _supportUrl, ...single } = setting;
  return [{ ...single, id: "default", label: "Sign in", default: true }];
}

/**
 * The three kinds of sign-in the sign-in page knows (round 10, C3): the workforce provider is
 * Microsoft, a google.com domain hint is Google, and anything else (External ID's own accounts)
 * signs in with an email address. One rule for the page (`client/src/pages/sign-in.tsx`) and the
 * release's sign-in check (`release/signInCheck.ts`, #1068), so both mean the same provider.
 */
export type SignInProviderKind = "email" | "microsoft" | "google";

export function signInProviderKind(provider: {
  idp: string;
  domainHint?: string | undefined;
}): SignInProviderKind {
  if (provider.idp === "entra-workforce") return "microsoft";
  if (provider.domainHint?.toLowerCase() === "google.com") return "google";
  return "email";
}

/**
 * The provider the page's email form starts: the default one among the email kind, else the
 * first of them (`undefined` when there is none).
 */
export function emailSignInProvider<T extends { idp: string; domainHint?: string | undefined }>(
  providers: readonly T[],
  isDefault: (provider: T) => boolean,
): T | undefined {
  const email = providers.filter((provider) => signInProviderKind(provider) === "email");
  return email.find(isDefault) ?? email[0];
}
